import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";

const FOOD_EMOJIS = ["🍔", "🍟", "🍕", "🍗", "🌮", "🍦", "🥤", "🥪"];
const GRID_SIZE = 5;
const MAX_MOVES = 12;
const POINTS_PER_MATCH = 10;

function getRandomEmoji(exclude = []) {
  // Exclude emojis that would cause a match
  const choices = FOOD_EMOJIS.filter((e) => !exclude.includes(e));
  return choices[Math.floor(Math.random() * choices.length)];
}

function generateGrid() {
  // Prevent pre-existing matches
  const grid = [];
  for (let row = 0; row < GRID_SIZE; row++) {
    grid[row] = [];
    for (let col = 0; col < GRID_SIZE; col++) {
      let exclude = [];
      // Check left two
      if (col >= 2 && grid[row][col - 1] === grid[row][col - 2]) {
        exclude.push(grid[row][col - 1]);
      }
      // Check up two
      if (row >= 2 && grid[row - 1][col] === grid[row - 2][col]) {
        exclude.push(grid[row - 1][col]);
      }
      grid[row][col] = getRandomEmoji(exclude);
    }
  }
  return grid;
}

function cloneGrid(grid) {
  return grid.map((row) => [...row]);
}

function isValidSwap(grid, r1, c1, r2, c2) {
  // Swap and check for match
  const newGrid = cloneGrid(grid);
  [newGrid[r1][c1], newGrid[r2][c2]] = [newGrid[r2][c2], newGrid[r1][c1]];
  return findMatches(newGrid).length > 0;
}

function findMatches(grid) {
  const found = [];
  // Horizontal
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE - 2; col++) {
      if (
        grid[row][col] === grid[row][col + 1] &&
        grid[row][col] === grid[row][col + 2]
      ) {
        found.push({ row, col }, { row, col: col + 1 }, { row, col: col + 2 });
      }
    }
  }
  // Vertical
  for (let col = 0; col < GRID_SIZE; col++) {
    for (let row = 0; row < GRID_SIZE - 2; row++) {
      if (
        grid[row][col] === grid[row + 1][col] &&
        grid[row][col] === grid[row + 2][col]
      ) {
        found.push({ row, col }, { row: row + 1, col }, { row: row + 2, col });
      }
    }
  }
  // Remove duplicates
  return found.filter(
    (pos, idx, arr) =>
      arr.findIndex((p) => p.row === pos.row && p.col === pos.col) === idx
  );
}

const Match3Game = () => {
  const [grid, setGrid] = useState(generateGrid());
  const [selected, setSelected] = useState(null); // {row, col}
  const [matches, setMatches] = useState([]); // [{row, col}]
  const [movesLeft, setMovesLeft] = useState(MAX_MOVES);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [animating, setAnimating] = useState(false);

  function handleTileClick(row, col) {
    if (gameOver || movesLeft <= 0 || animating) return;
    if (selected) {
      // Only allow swap with adjacent
      const dr = Math.abs(selected.row - row);
      const dc = Math.abs(selected.col - col);
      if (dr + dc === 1) {
        // Swap
        const newGrid = cloneGrid(grid);
        [newGrid[selected.row][selected.col], newGrid[row][col]] = [
          newGrid[row][col],
          newGrid[selected.row][selected.col],
        ];
        const found = findMatches(newGrid);
        setMovesLeft((prev) => prev - 1);
        if (found.length > 0) {
          setGrid(newGrid);
          setMatches(found);
          setScore(
            (prev) => prev + POINTS_PER_MATCH * Math.floor(found.length / 3)
          );
          setAnimating(true);
          setTimeout(() => {
            // Remove matched tiles and drop new ones
            const afterMatch = cloneGrid(newGrid);
            found.forEach(({ row, col }) => {
              afterMatch[row][col] = getRandomEmoji();
            });
            setGrid(afterMatch);
            setMatches([]);
            setAnimating(false);
          }, 700);
          // End game if no moves left after this move
          setTimeout(() => {
            if (movesLeft - 1 <= 0) setGameOver(true);
          }, 750);
        } else {
          // No match: swap, decrement moves, show message, but do NOT swap back
          setGrid(newGrid);
          setAnimating(false);
          setTimeout(() => {
            if (movesLeft - 1 <= 0) setGameOver(true);
          }, 750);
        }
        setSelected(null);
      } else {
        setSelected({ row, col });
      }
    } else {
      setSelected({ row, col });
    }
  }

  function handleReset() {
    setGrid(generateGrid());
    setSelected(null);
    setMatches([]);
    setMovesLeft(MAX_MOVES);
    setScore(0);
    setGameOver(false);
    setAnimating(false);
  }

  return (
    <Box
      sx={{
        background: "#FFF5F5",
        border: "4px solid #FF8C00",
        borderRadius: "24px",
        p: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: 320,
        maxWidth: 400,
      }}
    >
      <Typography variant="h5" sx={{ mb: 2, color: "black", fontWeight: 700 }}>
        🍟 Menu Match-3
      </Typography>
      <Typography sx={{ mb: 2, color: "#FF8C00", fontWeight: 500 }}>
        Classic puzzle game, customized with your brand's food items
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Typography sx={{ color: "#FF8C00", fontWeight: 600 }}>
          Moves Left: {movesLeft}
        </Typography>
        <Typography sx={{ color: "black", fontWeight: 600 }}>
          Score: {score}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${GRID_SIZE}, 40px)`,
          gridTemplateRows: `repeat(${GRID_SIZE}, 40px)`,
          gap: 1,
          mb: 2,
          pointerEvents: gameOver ? "none" : "auto",
          opacity: gameOver ? 0.5 : 1,
        }}
      >
        {grid.map((rowArr, row) =>
          rowArr.map((emoji, col) => {
            const isSelected =
              selected && selected.row === row && selected.col === col;
            const isMatched = matches.some(
              (m) => m.row === row && m.col === col
            );
            return (
              <Button
                key={`${row}-${col}`}
                onClick={() => handleTileClick(row, col)}
                sx={{
                  minWidth: 0,
                  width: 40,
                  height: 40,
                  fontSize: "1.6rem",
                  borderRadius: "12px",
                  border: isSelected
                    ? "2px solid #FF8C00"
                    : isMatched
                    ? "2px solid #4CAF50"
                    : "2px solid #FFF5F5",
                  background:
                    isMatched && animating
                      ? "#C8F7C5"
                      : isSelected
                      ? "#FFE0B2"
                      : "white",
                  boxShadow:
                    isMatched && animating
                      ? "0 0 12px 2px #4CAF50AA"
                      : isSelected
                      ? "0 0 6px 1px #FF8C0055"
                      : "0 1px 2px #0001",
                  transition: "all 0.2s cubic-bezier(.4,0,.2,1)",
                  m: 0,
                  p: 0,
                  transform:
                    isMatched && animating ? "scale(1.15)" : "scale(1)",
                  animation: isMatched && animating ? "shake 0.7s" : undefined,
                }}
              >
                {emoji}
              </Button>
            );
          })
        )}
      </Box>
      <style>{`
        @keyframes shake {
          0% { transform: scale(1.15) translateX(0); }
          20% { transform: scale(1.15) translateX(-4px); }
          40% { transform: scale(1.15) translateX(4px); }
          60% { transform: scale(1.15) translateX(-2px); }
          80% { transform: scale(1.15) translateX(2px); }
          100% { transform: scale(1.15) translateX(0); }
        }
      `}</style>
      {gameOver ? (
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Typography
            sx={{
              color: "#FF6B6B",
              fontWeight: 700,
              fontSize: "1.2rem",
              mb: 1,
            }}
          >
            Game Over!
          </Typography>
          <Typography
            sx={{
              color: "black",
              fontWeight: 600,
              fontSize: "1.1rem",
              mb: 2,
            }}
          >
            Your Score: {score}
          </Typography>
          <Button
            variant="contained"
            onClick={handleReset}
            sx={{
              background: "black",
              color: "#fff",
              fontWeight: 700,
              borderRadius: "100px",
              px: 4,
              mt: 1,
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              fontSize: "1.1rem",
              "&:hover": {
                background: "#FF8C00",
                color: "#fff",
              },
            }}
          >
            Play Again
          </Button>
        </Box>
      ) : (
        <Button
          variant="contained"
          onClick={handleReset}
          sx={{
            background: "#FF8C00",
            color: "#fff",
            fontWeight: 700,
            borderRadius: "100px",
            px: 4,
            mt: 1,
            textTransform: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            fontSize: "1.1rem",
            "&:hover": {
              background: "#FF8C00",
              color: "#fff",
            },
          }}
        >
          Try Again
        </Button>
      )}
    </Box>
  );
};

export default Match3Game;
